import React, { useState, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "src/components/ui/sheet";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { Input } from "src/components/ui/input";
import BorderedSelect from "src/components/form-new/bordered-select";
import type { CommissionAdjustment } from "../types/commissions.types";
import {
  useGetCommissionUsersQuery,
  useGetExternalRepsQuery,
} from "../endpoints/commissions.endpoints";

const TARGET_TYPE_OPTIONS = [
  { value: "user", label: "Activation Team user" },
  { value: "external_rep", label: "External rep" },
  { value: "internal_referral", label: "Internal referral" },
];

const QUARTER_OPTIONS = [
  { value: "1", label: "Q1" },
  { value: "2", label: "Q2" },
  { value: "3", label: "Q3" },
  { value: "4", label: "Q4" },
];

function getYearOptions(): { value: string; label: string }[] {
  const current = new Date().getFullYear();
  return [current - 1, current, current + 1].map((y) => ({
    value: String(y),
    label: String(y),
  }));
}

const YEAR_OPTIONS = getYearOptions();

interface AdjustmentDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (adjustment: Omit<CommissionAdjustment, "id">) => void;
}

const AdjustmentDrawer: React.FC<AdjustmentDrawerProps> = ({
  open,
  onOpenChange,
  onSave,
}) => {
  const [targetType, setTargetType] = useState<CommissionAdjustment["targetType"]>("user");
  const [targetId, setTargetId] = useState("");
  const [targetName, setTargetName] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [quarter, setQuarter] = useState("1");
  const [adjustmentKind, setAdjustmentKind] = useState<"amount" | "multiplier">("amount");
  const [amount, setAmount] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [reason, setReason] = useState("");

  const { data: users = [] } = useGetCommissionUsersQuery(undefined, { skip: !open });
  const { data: externalReps = [] } = useGetExternalRepsQuery(undefined, { skip: !open });

  const targetOptions = useMemo(() => {
    if (targetType === "user") {
      return users.map((u) => ({ value: u.id, label: u.name }));
    }
    if (targetType === "external_rep") {
      return externalReps.map((r) => ({ value: r.id, label: r.name }));
    }
    return [];
  }, [targetType, users, externalReps]);

  useEffect(() => {
    if (open) {
      setTargetType("user");
      setTargetId("");
      setTargetName("");
      setYear(String(new Date().getFullYear()));
      setQuarter("1");
      setAdjustmentKind("amount");
      setAmount("");
      setMultiplier("");
      setReason("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const match = targetOptions.find((o) => o.value === targetId);
    if (match) setTargetName(match.label);
  }, [targetId, targetOptions, open]);

  const handleSave = () => {
    const selected = targetOptions.find((o) => o.value === targetId);
    const payload: Omit<CommissionAdjustment, "id"> = {
      targetType,
      targetId: targetId.trim(),
      targetName: selected?.label ?? targetName.trim(),
      year: Number(year),
      quarter: Number(quarter),
      reason: reason.trim(),
      ...(adjustmentKind === "amount" && amount.trim()
        ? { amount: parseFloat(amount) }
        : {}),
      ...(adjustmentKind === "multiplier" && multiplier.trim()
        ? { multiplier: parseFloat(multiplier) }
        : {}),
    };
    onSave(payload);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!w-1/2 !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle className={portalDs.sheetTitle}>Add adjustment</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Target type</label>
            <BorderedSelect
              label=""
              options={TARGET_TYPE_OPTIONS}
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value as CommissionAdjustment["targetType"]);
                setTargetId("");
                setTargetName("");
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Target</label>
            {targetType === "internal_referral" ? (
              <Input
                value={targetName}
                onChange={(e) => {
                  setTargetName(e.target.value);
                  setTargetId(e.target.value);
                }}
                placeholder="Internal referral name"
                className={portalDs.input}
              />
            ) : (
              <BorderedSelect
                label=""
                options={[
                  { value: "", label: "Select target…" },
                  ...targetOptions,
                ]}
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
              />
            )}
          </div>
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col gap-1.5">
              <label className={portalDs.label}>Year</label>
              <BorderedSelect
                label=""
                options={YEAR_OPTIONS}
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <label className={portalDs.label}>Quarter</label>
              <BorderedSelect
                label=""
                options={QUARTER_OPTIONS}
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Adjustment type</label>
            <BorderedSelect
              label=""
              options={[
                { value: "amount", label: "Fixed amount" },
                { value: "multiplier", label: "Multiplier (e.g. 0.97 for 3% penalty)" },
              ]}
              value={adjustmentKind}
              onChange={(e) => setAdjustmentKind(e.target.value as "amount" | "multiplier")}
            />
          </div>
          {adjustmentKind === "amount" ? (
            <div className="flex flex-col gap-1.5">
              <label className={portalDs.label}>Amount ($)</label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. -500"
                className={`${portalDs.input} max-w-xs`}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <label className={portalDs.label}>Multiplier</label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
                placeholder="e.g. 0.97"
                className={`${portalDs.input} max-w-xs`}
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Reason</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Q2 3% penalty"
              className={portalDs.input}
            />
          </div>
        </div>
        <SheetFooter>
          <PortalDsButton type="button" variant="primary" size="sm" className="px-6" onClick={handleSave}>
            Save
          </PortalDsButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AdjustmentDrawer;
